import React, { Component, Children } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import { AngleDownIcon } from '@wf-sl/common-icons'

import { ErrorType } from '../../prop-types/error-prop-type'
import T3Constants from '../constants'
import { FilteredSelect } from '../select/filtered-select'
import Select from '../select/select'
import Tag from '../tag/tag'
import TagWrapper from '../tag/tag-wrapper'
import virtualListPropType from '../virtual-list/virtual-list-prop-type'
import Preselector from './preselector'

import styles from './tag-dropdown.module.css'

const propTypes = {
    disabled: PropTypes.bool,
    dropdownOpen: PropTypes.bool,
    error: ErrorType,
    expandPreselection: PropTypes.bool,
    filterPlaceholder: PropTypes.string,
    filterable: PropTypes.bool,
    groupTagsThreshold: PropTypes.number,
    icon: PropTypes.node,
    name: PropTypes.string,
    onChange: PropTypes.func,
    optionListPlacement: PropTypes.oneOf(['top', 'bottom']),
    placeholder: PropTypes.string,
    value: PropTypes.array,
    virtual: virtualListPropType
}

const defaultProps = {
    disabled: false,
    dropdownOpen: false,
    error: null,
    filterable: false,
    groupTagsThreshold: 2,
    icon: null,
    name: null,
    onChange() {},
    optionListPlacement: 'bottom',
    value: []
}

/**
 * Extends Dropdown to use filterable list component.
 *
 * Usage:
 * <TagDropdown>
 *     <Checkbox value={0}>A</Checkbox>
 *     <Checkbox value={1}>B</Checkbox>
 *     <Checkbox value={2}>C</Checkbox>
 * </TagDropdown>
 *
 * @param {Object} props - The current component props
 * @param {boolean} [disabled=false] - Is the dropdown currently disabled?
 * @param {boolean} [dropdownOpen=false] - Is the dropdown currently open?
 * @param {ErrorType} [error=null] - Error associated with the dropdown
 * @param {boolean} [expandPreselection=false] - Brake down preselection instead of combine them to one element
 * @param {number} [groupTagsThreshold=2] - After how many tags are they grouped together into one
 * @param {object} [icon=null] - Icon to decorate the header with
 * @param {string} [name=null] - Name of the component (used in callback)
 * @param {Function} [onChange=()=>{}] - Callback fired when selection changes
 * @param {string} [optionListPlacement='bottom'] - Placement of the options list: 'bottom' or 'top'
 * @param {boolean} [filterable=false] - Should a filter search box be displayed at the top of the list?
 * @param {Object[]} [value=[]] - Array of the corresponding Option values that should be selected
 * @param {VirtualList} [virtual] - Settings to create/enable a virtual list
 */
class TagDropdown extends Component {
    constructor(props) {
        super(props)
        this.state = {
            value: this.props.value
        }
        this.onSelectionChanged = this.onSelectionChanged.bind(this)
        this.onRemoveTagItem = this.onRemoveTagItem.bind(this)
        this.onPreselect = this.onPreselect.bind(this)
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { value } = nextProps
        this.setState({ value })
    }

    render() {
        const { className, filterable, virtual, ...props } = this.props
        const { value } = this.state
        const { checkedPreselectors, selectChildren } = this.getChildren()

        const selectProps = Object.assign({}, props, {
            className: cx(styles['tag-dropdown'], className),
            header: this.renderHeader(checkedPreselectors),
            multiple: true,
            onChange: this.onSelectionChanged,
            ref: ref => (this.select = ref),
            value,
            virtual
        })

        if (filterable) {
            return <FilteredSelect {...selectProps}>{selectChildren}</FilteredSelect>
        }
        return <Select {...selectProps}>{selectChildren}</Select>
    }

    renderHeader(checkedPreselectors) {
        const {
            disabled,
            dropdownOpen,
            error,
            expandPreselection,
            groupTagsThreshold,
            icon,
            optionListPlacement,
            placeholder
        } = this.props

        const mergedProps = {
            disabled,
            dropdownOpen,
            error,
            expandPreselection,
            groupTagsThreshold,
            icon,
            onRemoveItem: this.onRemoveTagItem,
            optionListPlacement,
            placeholder
        }

        return <TagSelectHeader {...{ checkedPreselectors, ...mergedProps }} />
    }

    /**
     * This method augments the children and returns arrays of react elements to be used in the select component.
     * It also returns an array of preselectors that are currently checked, which is used in the header.
     *
     * @returns {{selectChildren: Array, checkedPreselectors: Array}}
     */
    getChildren() {
        const { children, virtual } = this.props
        const { value } = this.state

        // This constant is used to track which preselectors are checked at this moment
        const checkedPreselectors = []

        const selectChildren = Children.map(children, (child, index) => {
            let { selectable } = child.props
            if (typeof selectable === 'undefined') {
                selectable = true
            }
            const className = cx(child.props.className, styles['tag-dropdown-list-item'], {
                [styles['virtual-child']]: virtual
            })
            const props = {
                changeEvent: 'onChange',
                className,
                selectable,
                selectionProp: 'checked'
            }

            // We add additional props to the preselector children to be able to react
            // on their changes and pass the current selection.
            if (child.type === Preselector) {
                props.onPreselect = this.onPreselect
                props.selection = value
                if (child.props.value.every(i => value.indexOf(i) >= 0)) {
                    checkedPreselectors.push(child)
                }
            }

            return React.cloneElement(child, props)
        })

        return { checkedPreselectors, selectChildren }
    }

    onRemoveTagItem(removeValue) {
        const { name, onChange } = this.props
        // Since the preselectors use this callback and pass an array of values,
        // we need to check for array here as well.
        const filter = Array.isArray(removeValue)
            ? selectedValue => removeValue.indexOf(selectedValue) < 0
            : selectedValue => selectedValue !== removeValue
        const value = this.state.value.filter(filter)
        this.setState({ value }, () => onChange({ target: { name, value } }))
    }

    onSelectionChanged(event) {
        const { name, onChange } = this.props
        const { value } = event.target
        this.setState({ value })
        onChange({ target: { name, value } })
    }

    onPreselect(preselectValue, selected) {
        const { name, onChange } = this.props
        const { value: currentValue } = this.state
        let value = null
        if (selected) {
            value = [...new Set(currentValue.concat(preselectValue))]
        } else {
            value = currentValue.filter(v => preselectValue.indexOf(v) < 0)
        }
        this.setState({ value }, () => onChange({ target: { name, value } }))
    }
}

TagDropdown.propTypes = propTypes
TagDropdown.defaultProps = defaultProps

const tagSelectHeaderPropTypes = {
    checkedPreselectors: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    dropdownOpen: PropTypes.bool,
    error: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    expandPreselection: PropTypes.bool,
    groupTagsThreshold: PropTypes.number,
    icon: PropTypes.node,
    onClick: PropTypes.func,
    onRemoveItem: PropTypes.func,
    optionListPlacement: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.node),
    placeholder: PropTypes.string,
    selectedOptions: PropTypes.arrayOf(PropTypes.node)
}

const tagSelectHeaderDefaultProps = {
    dropdownOpen: false,
    expandPreselection: false,
    groupTagsThreshold: 2,
    onClick() {},
    onRemoveItem() {},
    optionListPlacement: 'bottom'
}

class TagSelectHeader extends Component {
    constructor(props) {
        super(props)

        this.onRemoveTag = this.onRemoveTag.bind(this)
    }

    render() {
        const {
            checkedPreselectors,
            disabled,
            dropdownOpen,
            error,
            expandPreselection,
            icon,
            onClick,
            optionListPlacement,
            selectedOptions
        } = this.props
        const classNameMerged = cx('t3sel-tag-dropdown-header', styles['tag-dropdown-header'], {
            [styles['disabled']]: disabled,
            [styles['error']]: !disabled && error,
            [styles['open']]: dropdownOpen
        })
        const buttonClassName = cx('t3sel-tag-dropdown-button', styles['tag-dropdown-button'], {
            [styles['angle-up']]: optionListPlacement === 'top'
        })

        // Create a list of all values checked by the preselectors
        let preselectedValues = []

        // Create a list of preselectors to display.
        // If a preselector's values are already covered by an already displayed preselector,
        // then do not show it.
        const preselectors = !expandPreselection
            ? checkedPreselectors.reduce((list, item) => {
                  const newValues = item.props.value.filter(val => preselectedValues.indexOf(val) < 0)
                  if (newValues.length) {
                      preselectedValues = [...preselectedValues, ...newValues]
                      return [...list, item]
                  } else {
                      return list
                  }
              }, [])
            : []

        // Only display tags, whose values were not covered by any preselector.
        const tagItems = !expandPreselection
            ? selectedOptions.filter(item => preselectedValues.indexOf(item.props.value) < 0)
            : selectedOptions

        return (
            <div className={classNameMerged} onClick={onClick}>
                {icon && <div className={styles['tag-dropdown-icon']}>{icon}</div>}
                <span className={styles['tag-dropdown-content']}>
                    {this.renderTags([...preselectors, ...tagItems])}
                </span>
                <AngleDownIcon className={buttonClassName} />
            </div>
        )
    }

    renderTags(items) {
        const { groupTagsThreshold, options, placeholder } = this.props
        const placeholderString = placeholder ? <div>{placeholder}</div> : null

        if (!items.length && options.length) {
            return (
                <div className={styles['tag-dropdown-placeholder']}>
                    {placeholderString || options.shift().props.children}
                </div>
            )
        }

        const renderItem = item => {
            const affix = item.props.affix ? (
                <div className={styles['affix']}>{` ${T3Constants.HtmlEntities.NDASH} ${item.props.affix}`}</div>
            ) : (
                ''
            )

            return (
                <Tag
                    className={styles['tag-dropdown-tag']}
                    icon={item.props.icon}
                    indicatorColor={item.props.tagColor}
                    key={item.props.value}
                    onClose={event => this.onRemoveTag(item, event)}
                >
                    {item.props.summary ? item.props.summary : item.props.children}
                    {affix}
                </Tag>
            )
        }
        return (
            <TagWrapper
                key="tag-dropdown-tag-wrapper"
                className={styles['width-300-class']}
                renderItem={renderItem}
                maxTooltipItems={groupTagsThreshold}
                items={items}
                tagPadding={5}
            />
        )
    }

    onRemoveTag(item, event) {
        const { onRemoveItem } = this.props

        if (!this.props.disabled) {
            event && event.stopPropagation()
            onRemoveItem(item.props.value)
        }
    }
}

TagSelectHeader.propTypes = tagSelectHeaderPropTypes
TagSelectHeader.defaultProps = tagSelectHeaderDefaultProps

export default TagDropdown
